import { useEffect, useRef } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';

interface UseFormInitializationProps {
  copiedInvoiceData: any;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  setTableRowsByFreelancer: React.Dispatch<React.SetStateAction<{ [freelancerId: string]: any[] }>>;
  setCalendarSelectedDatesByFreelancer: React.Dispatch<React.SetStateAction<{ [freelancerId: string]: Date[] }>>;
  setPensionDataByFreelancer: React.Dispatch<React.SetStateAction<{ [freelancerId: string]: { pension: boolean; pensionAmount: number } }>>;
  setHolidayDataByFreelancer: React.Dispatch<React.SetStateAction<{ [freelancerId: string]: { holidaySavings: boolean; holidaySavingsFee: number } }>>;
  setPensionPay: React.Dispatch<React.SetStateAction<boolean>>;
  setHolidayPay: React.Dispatch<React.SetStateAction<boolean>>;
  setExpressPayments: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Custom hook to handle form initialization from copiedInvoiceData
 * Extracts and processes working hours, pension, holiday savings, and other invoice data
 * @param props - Configuration object containing copiedInvoiceData, setValue, and state setters
 */
export const useFormInitialization = ({
  copiedInvoiceData,
  setValue,
  watch,
  setTableRowsByFreelancer,
  setCalendarSelectedDatesByFreelancer,
  setPensionDataByFreelancer,
  setHolidayDataByFreelancer,
  setPensionPay,
  setHolidayPay,
  setExpressPayments,
}: UseFormInitializationProps) => {
  
  // Map article number to pricing type
  const getPricingTypeFromArticleNumber = (articleNumber: string): string | null => {
    const articleMap: { [key: string]: string } = {
      '92': 'Tim',      // Hourly Wages
      '94': 'Dag',       // Payment Per Day
      '95': 'vecka',      // Payment Per Week
      '96': 'm',     // Payment Per Month
      '97': 'a'  // Payment As Agreed
    };
    return articleMap[articleNumber] || null;
  };

  // Check if article number is valid (only these article numbers should be processed)
  const isValidArticleNumber = (articleNumber: string): boolean => {
    const validArticles = ['92', '94', '95', '96', '97'];
    return validArticles.includes(articleNumber);
  };

  useEffect(() => {
    // console.log('📝 useFormInitialization - copiedInvoiceData changed:', copiedInvoiceData);
  }, [copiedInvoiceData]);

  // Track if we've already initialized from copiedInvoiceData to prevent re-initialization
  const hasInitializedRef = useRef<boolean>(false);
  const prevCopiedInvoiceDataRef = useRef<any>(null);
  const prevDataHashRef = useRef<string>('');
  const lastInitializedInvoiceIdRef = useRef<string | number | null>(null);

  // Log copiedInvoiceData when it changes and populate data from workingHours
  useEffect(() => {
    if (!copiedInvoiceData) {
      hasInitializedRef.current = false;
      prevCopiedInvoiceDataRef.current = null;
      prevDataHashRef.current = '';
      lastInitializedInvoiceIdRef.current = null;
      return;
    }

    // Create a hash of critical data to detect actual changes (ignoring object reference changes)
    // Use workingHours array length and first/last item IDs to detect changes
    const currentDataHash = JSON.stringify({
      id: copiedInvoiceData.id,
      invoiceNumber: copiedInvoiceData.invoiceNumber,
      workingHoursLength: copiedInvoiceData.workingHours?.length || 0,
      firstWorkingHourId: copiedInvoiceData.workingHours?.[0]?.id,
      lastWorkingHourId: copiedInvoiceData.workingHours?.[copiedInvoiceData.workingHours.length - 1]?.id,
      createdDate: copiedInvoiceData.createdDate,
      multipleFreelancer: copiedInvoiceData.multipleFreelancer
    });

    // Check if this is a DIFFERENT invoice ID (new preId/invoice being loaded)
    const isDifferentInvoice = lastInitializedInvoiceIdRef.current !== copiedInvoiceData.id;
    const hasDataActuallyChanged = prevDataHashRef.current !== currentDataHash;
    
    // If it's a different invoice, always reinitialize (reset the hash to force re-init)
    if (isDifferentInvoice) {
       hasInitializedRef.current = false;
      prevDataHashRef.current = ''; // Reset hash to force initialization
    }
    
    // Only skip if we've already initialized THIS specific invoice AND data hasn't changed
    if (hasInitializedRef.current && !hasDataActuallyChanged && !isDifferentInvoice) {
       return;
    }
    
    // If data changed or not yet initialized, proceed with initialization
    if ((hasDataActuallyChanged || isDifferentInvoice) && prevDataHashRef.current !== '') {
      hasInitializedRef.current = false;
      }

    // Extract workingHours from copied invoice data
    if (copiedInvoiceData.workingHours && Array.isArray(copiedInvoiceData.workingHours) && copiedInvoiceData.workingHours.length > 0) {
        const workingHours = copiedInvoiceData.workingHours;
        const isMultipleFreelancer = copiedInvoiceData.multipleFreelancer || false;
        
        // Filter working hours to only include valid article numbers
        const filteredWorkingHours = workingHours.filter((wh: any) => {
          const isValid = isValidArticleNumber(wh.articleNumber);
          return isValid;
        });
      
        // Group working hours by freelancer (userId) - using filtered data
        const workingHoursByFreelancer: { [key: string]: any[] } = {};
        
        filteredWorkingHours.forEach((wh: any) => {
          
          // Properly handle userId - treat null/undefined differently from 0
          let freelancerId: string;
          
          if (isMultipleFreelancer) {
            // In multiple freelancer mode, userId should exist
            if (wh.userId !== null && wh.userId !== undefined) {
              freelancerId = wh.userId.toString();
            } else {
              // If userId is missing in multiple freelancer mode, assign to 'default'
              // This will be matched later with invoiceFreelancer data
              freelancerId = 'default';
            }
          } else {
            // Single freelancer mode - always use 'default'
            freelancerId = 'default';
          }
          
          if (!workingHoursByFreelancer[freelancerId]) {
            workingHoursByFreelancer[freelancerId] = [];
          }
          
          workingHoursByFreelancer[freelancerId].push(wh);
        });
        
        
        // Log all freelancer IDs from invoiceFreelancer for comparison
        // if (copiedInvoiceData.invoiceFreelancer && Array.isArray(copiedInvoiceData.invoiceFreelancer)) {
        //   console.log('👥 [FREELANCERS] Available in invoiceFreelancer:', 
        //     copiedInvoiceData.invoiceFreelancer.map((f: any) => `userId: ${f.userId}, name: ${f.freelancerDetails?.firstName} ${f.freelancerDetails?.lastName}`).join(', '));
        // }
        
        // Process each freelancer's working hours
        const newTableRowsByFreelancer: { [key: string]: any[] } = {};
        const newCalendarDatesByFreelancer: { [key: string]: Date[] } = {};
        const newPensionDataByFreelancer: { [key: string]: any } = {};
        const newHolidayDataByFreelancer: { [key: string]: any } = {};
       
        
        Object.entries(workingHoursByFreelancer).forEach(([freelancerId, freelancerWorkingHours]) => {
          const allDates: Date[] = [];
          const tableRows: any[] = [];
          
          freelancerWorkingHours.forEach((wh: any, index: number) => {
            if (!wh.workingDate) return;
            const dateKey = wh.workingDate.split('T')[0]; // Get date part only
            const dateObj = new Date(dateKey);
            
            // Add to dates array for calendar
            if (!allDates.some(d => d.toDateString() === dateObj.toDateString())) {
              allDates.push(dateObj);
            }
            
            // Map working hour data to table row format
            // If isUnitArticle is true, use 'st' (Payment Per Unit); both 'a' and 'st' share article number 97
            const pricingType = wh.isUnitArticle === true
              ? 'st'
              : (getPricingTypeFromArticleNumber(wh.articleNumber || '92') || 'Tim');
            const isHourlyType = pricingType === 'Tim';
            
            // Format time based on pricing type
            let timeValue = '';
            if (isHourlyType) {
              // For hourly: workingHour is in decimal hours, convert to hh:mm
              // Default to 8 hours if workingHour is 0 or not provided
              const workingHourValue = wh.workingHour && wh.workingHour > 0 ? wh.workingHour : 8;
              const hours = Math.floor(workingHourValue);
              const minutes = Math.round((workingHourValue - hours) * 60);
              timeValue = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            } else {
              // For day/week/month/agreement: use number (units)
              timeValue = (wh.number || 1).toString();
            }
            
            const row = {
              id: wh.id || 0,
              workingHourId: wh.id || 0, // Preserve original working hour ID from API
              date: new Date(dateKey).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
              }),
              pricingType: pricingType,
              pricingTypeArticleNumber: wh.articleNumber || '92',
              service: wh.houseworkType ? wh.houseworkType.toLowerCase() : (wh.workingDescription || ''),
              time: timeValue,
              price: (wh.amountPerUnit || 0).toString(),
              discount: { value: 0, type: 'percentage' as const },
              subtotal: wh.amount || 0,
              customWorkingDescription: wh.customWorkingDescription || ''
            };
           tableRows.push(row);
          });

          // Sort rows by date
          tableRows.sort((a: any, b: any) => {
            return new Date(a.date).getTime() - new Date(b.date).getTime();
          });
          
          // Store table rows for this freelancer
          newTableRowsByFreelancer[freelancerId] = tableRows;
          
          // Sort and store calendar dates for this freelancer
          const sortedDates = allDates.sort((a, b) => a.getTime() - b.getTime());
          newCalendarDatesByFreelancer[freelancerId] = sortedDates;
          
          // Get pension and holiday data for this freelancer from invoiceFreelancer array
          if (copiedInvoiceData.invoiceFreelancer && Array.isArray(copiedInvoiceData.invoiceFreelancer)) {
            // Find freelancer data by userId - handle both number and string comparison
            const freelancerData = copiedInvoiceData.invoiceFreelancer.find((f: any) => {
              // Handle null/undefined userId
              if (f.userId === null || f.userId === undefined) {
                return freelancerId === 'default';
              }
              // Compare as strings to handle type mismatches
              return f.userId?.toString() === freelancerId.toString();
            });
            
            if (freelancerData) {
              newPensionDataByFreelancer[freelancerId] = {
                pension: freelancerData.pension || false,
                pensionAmount: freelancerData.pensionAmount || 0
              };
              
              newHolidayDataByFreelancer[freelancerId] = {
                holidaySavings: freelancerData.holidaySavings || false,
                holidaySavingsFee: freelancerData.holidaySavingsFee || 0
              };
            } else {
              // Set default values if no data found
              newPensionDataByFreelancer[freelancerId] = {
                pension: false,
                pensionAmount: 0
              };
              
              newHolidayDataByFreelancer[freelancerId] = {
                holidaySavings: false,
                holidaySavingsFee: 0
              };
            }
          }
        });
        
        // Update all states once after processing all freelancers
        setTableRowsByFreelancer(newTableRowsByFreelancer);
        setCalendarSelectedDatesByFreelancer(newCalendarDatesByFreelancer);
        setPensionDataByFreelancer(newPensionDataByFreelancer);
        setHolidayDataByFreelancer(newHolidayDataByFreelancer);
        
        // Mark as initialized after state updates
        hasInitializedRef.current = true;
        prevCopiedInvoiceDataRef.current = copiedInvoiceData;
        lastInitializedInvoiceIdRef.current = copiedInvoiceData.id; // Track which invoice we initialized
        // Update the hash to mark this data as processed
        prevDataHashRef.current = JSON.stringify({
          id: copiedInvoiceData.id,
          invoiceNumber: copiedInvoiceData.invoiceNumber,
          workingHoursLength: copiedInvoiceData.workingHours?.length || 0,
          firstWorkingHourId: copiedInvoiceData.workingHours?.[0]?.id,
          lastWorkingHourId: copiedInvoiceData.workingHours?.[copiedInvoiceData.workingHours.length - 1]?.id,
          createdDate: copiedInvoiceData.createdDate,
          multipleFreelancer: copiedInvoiceData.multipleFreelancer
        });
        
        // Set date range in the form (using first freelancer's dates or default)
        const firstFreelancerId = Object.keys(newCalendarDatesByFreelancer)[0];
        if (firstFreelancerId && newCalendarDatesByFreelancer[firstFreelancerId].length > 0) {
          const sortedDates = newCalendarDatesByFreelancer[firstFreelancerId];
          const formatDate = (date: Date) => {
            return date.toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric'
            });
          };
          
          const dateRangeText = sortedDates.length > 1
            ? `${formatDate(sortedDates[0])} - ${formatDate(sortedDates[sortedDates.length - 1])}`
            : formatDate(sortedDates[0]);
          
          setValue('workDates', dateRangeText);
        }
        
        // Always set hours per day to default '0.00' when getting data
        // User should manually fill this field, not auto-filled from API
        setValue('hoursPerDayType', '0.00');  
        
        // Set show dates value
        if (copiedInvoiceData.showDates !== undefined) {
          setValue('showDates', copiedInvoiceData.showDates === true || copiedInvoiceData.showDates === 'true' ? 'yes' : 'no');
        } else {
          // Default to 'yes' if not specified
          setValue('showDates', 'yes');
        }
        
        // Set pension and holiday savings for default/single freelancer view
        if (!isMultipleFreelancer) {
          if (copiedInvoiceData.pension !== undefined) {
            setPensionPay(copiedInvoiceData.pension);
            setValue('pension', copiedInvoiceData.pension);
          }
          
          if (copiedInvoiceData.holidaySavings !== undefined) {
            setHolidayPay(copiedInvoiceData.holidaySavings);
            setValue('holidaySavings', copiedInvoiceData.holidaySavings);
          }
        }
        
        // Set express payout if available
        if (copiedInvoiceData.expressPayout !== undefined) {
          setExpressPayments(copiedInvoiceData.expressPayout);
          setValue('expressPayout', copiedInvoiceData.expressPayout);
        }
      } else {
        // No working hours in copiedInvoiceData, but still set pension and holiday if available
        console.log('⚠️ useFormInitialization - No workingHours data found, checking for pension/holiday data');
        
        // Set pension and holiday savings from invoiceFreelancer data if available
        if (copiedInvoiceData.invoiceFreelancer && Array.isArray(copiedInvoiceData.invoiceFreelancer)) {
          const newPensionData: { [key: string]: any } = {};
          const newHolidayData: { [key: string]: any } = {};
          
          copiedInvoiceData.invoiceFreelancer.forEach((f: any) => {
            const freelancerId = f.userId?.toString() || 'default';
            newPensionData[freelancerId] = {
              pension: f.pension || false,
              pensionAmount: f.pensionAmount || 0
            };
            newHolidayData[freelancerId] = {
              holidaySavings: f.holidaySavings || false,
              holidaySavingsFee: f.holidaySavingsFee || 0
            };
          });
          
          setPensionDataByFreelancer(newPensionData);
          setHolidayDataByFreelancer(newHolidayData);
          
          console.log('✅ useFormInitialization - Pension/Holiday data set from invoiceFreelancer');
        }
        
        // Still mark as initialized
        hasInitializedRef.current = true;
        prevCopiedInvoiceDataRef.current = copiedInvoiceData;
        lastInitializedInvoiceIdRef.current = copiedInvoiceData.id; // Track which invoice we initialized
        // Update the hash
        prevDataHashRef.current = JSON.stringify({
          id: copiedInvoiceData.id,
          invoiceNumber: copiedInvoiceData.invoiceNumber,
          workingHoursLength: copiedInvoiceData.workingHours?.length || 0,
          firstWorkingHourId: copiedInvoiceData.workingHours?.[0]?.id,
          lastWorkingHourId: copiedInvoiceData.workingHours?.[copiedInvoiceData.workingHours.length - 1]?.id,
          createdDate: copiedInvoiceData.createdDate,
          multipleFreelancer: copiedInvoiceData.multipleFreelancer
        });
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [copiedInvoiceData, setValue, setTableRowsByFreelancer, setCalendarSelectedDatesByFreelancer, setPensionDataByFreelancer, setHolidayDataByFreelancer, setPensionPay, setHolidayPay, setExpressPayments]);

  // Set default value for showDates on initial mount
  const hasSetDefaultShowDatesRef = useRef<boolean>(false);
  useEffect(() => {
    if (!hasSetDefaultShowDatesRef.current) {
      // Set default value without checking current value to avoid watch() issues
      setValue('showDates', 'yes');
      hasSetDefaultShowDatesRef.current = true;
    }
  }, [setValue]);
};
